import { api } from './api';
import { handleUnauthorized } from './sessionGuard';

type MessageCallback = (event: string, data: unknown) => void;
type ConnectionCallback = () => void;

interface WebSocketMessage {
  event: string;
  data: unknown;
}

// Backend close code for a rejected/expired JWT (connection_manager._verify_token,
// backend/src/websocket/manager.py). Blind-retrying this code with the same
// token fails identically every time until maxReconnectAttempts is exhausted,
// then the socket dies silently — see handleAuthRejection().
const WS_CODE_INVALID_TOKEN = 4001;

// WebSocket URL — the backend endpoint is /api/v1/ws (main.py's
// @app.websocket) and every proxy layer forwards that exact path with Upgrade
// headers. The original MVP client pointed at a bare /ws that no proxy or
// backend ever served, so realtime WS had never connected in any environment
// until this was corrected (2026-07-07 audit). Same origin as all REST calls;
// prod ingress must permit the Upgrade handshake on /api/v1/ws.
const getWsUrl = (): string => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  return `${protocol}//${host}/api/v1/ws`;
};

// `crypto.randomUUID()` is only defined in a secure context (HTTPS or
// localhost). Plain-HTTP LAN access (e.g. a phone hitting the dev server by
// IP) makes it `undefined`, and calling it throws — crashing the WebSocket
// client before the first connection attempt. `crypto.getRandomValues` has
// no such restriction, so fall back to hand-assembling a v4 UUID from it.
export function generateClientId(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private token: string | null = null;
  // Stable per tab session: the backend requires a unique client_id and keys
  // its connection registry on it. Memoizing here (rather than regenerating per
  // establishConnection) means every reconnect attempt presents the same
  // identity, so the server replaces the prior socket instead of accumulating
  // orphans. A fresh page load — a new service instance — gets a new id.
  private clientId: string | null = null;
  private messageCallbacks: Set<MessageCallback> = new Set();
  private connectCallbacks: Set<ConnectionCallback> = new Set();
  private disconnectCallbacks: Set<ConnectionCallback> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private baseReconnectDelay = 1000; // 1 second
  private maxReconnectDelay = 30000; // 30 seconds
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private isIntentionalDisconnect = false;

  connect(token: string): void {
    // Prevent multiple connections
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.token = token;
    this.isIntentionalDisconnect = false;
    this.establishConnection();
  }

  private establishConnection(): void {
    if (!this.token) {
      return;
    }

    // Clean up existing connection
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
    }

    // Both params are required by the backend; encodeURIComponent keeps the
    // token intact through the proxy chain (JWTs are URL-safe, but callers may
    // pass values that are not).
    const clientId = encodeURIComponent(this.getClientId());
    const token = encodeURIComponent(this.token);
    this.ws = new WebSocket(`${getWsUrl()}?client_id=${clientId}&token=${token}`);

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.connectCallbacks.forEach((callback) => callback());
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data as string) as WebSocketMessage;
        this.messageCallbacks.forEach((callback) => callback(message.event, message.data));
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onclose = (event: CloseEvent) => {
      this.disconnectCallbacks.forEach((callback) => callback());

      if (this.isIntentionalDisconnect) {
        return;
      }

      if (event.code === WS_CODE_INVALID_TOKEN) {
        this.handleAuthRejection();
        return;
      }

      this.scheduleReconnect();
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }

  private getClientId(): string {
    if (this.clientId === null) {
      this.clientId = generateClientId();
    }
    return this.clientId;
  }

  /**
   * The backend rejected the token we connected with (close code 4001).
   * Re-resolve the current token from the REST layer before doing anything
   * else — it may have refreshed since this socket was opened. Only retry
   * with that freshly-resolved token if it actually differs from the one
   * that was just rejected; otherwise blind-retrying would fail identically
   * up to maxReconnectAttempts and then die silently, killing realtime
   * updates for the rest of the tab's life. In that case, stop and surface
   * the failure through the same session-guard path a REST 401 would use.
   */
  private handleAuthRejection(): void {
    const freshToken = api.getToken();

    if (freshToken && freshToken !== this.token) {
      this.token = freshToken;
      this.reconnectAttempts = 0;
      this.establishConnection();
      return;
    }

    console.error('WebSocket auth rejected (4001); not retrying with the same token');
    void handleUnauthorized();
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    // Exponential backoff with jitter
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts) + Math.random() * 1000,
      this.maxReconnectDelay
    );

    this.reconnectAttempts++;

    this.reconnectTimeoutId = setTimeout(() => {
      this.establishConnection();
    }, delay);
  }

  disconnect(): void {
    this.isIntentionalDisconnect = true;

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.token = null;
    this.reconnectAttempts = 0;
  }

  send(event: string, data: unknown): void {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket is not connected');
      return;
    }

    const message: WebSocketMessage = { event, data };
    this.ws.send(JSON.stringify(message));
  }

  onMessage(callback: MessageCallback): () => void {
    this.messageCallbacks.add(callback);
    return () => {
      this.messageCallbacks.delete(callback);
    };
  }

  onConnect(callback: ConnectionCallback): () => void {
    this.connectCallbacks.add(callback);
    return () => {
      this.connectCallbacks.delete(callback);
    };
  }

  onDisconnect(callback: ConnectionCallback): () => void {
    this.disconnectCallbacks.add(callback);
    return () => {
      this.disconnectCallbacks.delete(callback);
    };
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

export const websocketService = new WebSocketService();
